import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import TpDataImportExcludedItem from '../../../Domain/TpDataImportExcludedItem';
import TpDataImportExcludedItemService from '../../../Services/TpDataImportExcludedItemService';

// GeneralEditPanelBase (Figma) description for creating/updating an import-excluded item.
// The Third-Party Instance field uses the self-loading ThirdPartySourceSelect input.
export default class TpDataImportExcludedItemPanelDescription {
  static itemType = TpDataImportExcludedItem;
  static headerForEditPanel = 'Edit Import Excluded Item';
  static headerForCreatePanel = 'Create Import Excluded Item';
  static labelForSubmitButton = 'Save';
  static nameOfTheIdField = 'TpDataImportExcludedItemId';
  static icon = 'fa-solid fa-filter-circle-xmark';
  static messageOnSuccess = 'The import excluded item has been saved.';

  static clickOnSubmitFunction = (editedItem) => TpDataImportExcludedItemService.UpsertItem(editedItem);

  // For now TpEvaluatorLink is the only supported entity type (matches the DB CHECK constraint and
  // the backend EntityTypes guard). It is the default, so the create panel opens with it selected.
  static getEntityTypeOptions() {
    return [{ label: 'TpEvaluatorLink', value: 'TpEvaluatorLink', disabled: false }];
  }

  static getPatternTypeOptions() {
    return [
      { label: 'Choose one...', value: '', disabled: false },
      { label: 'ThirdPartyId', value: 'ThirdPartyId', disabled: false },
      { label: 'FullEmail', value: 'FullEmail', disabled: false },
      { label: 'EmailDomain', value: 'EmailDomain', disabled: false },
    ];
  }

  // Yes/No only (no empty "Choose one"); the item defaults to Active = true.
  static getActiveOptions() {
    return [
      { label: 'Yes', value: true, disabled: false },
      { label: 'No', value: false, disabled: false },
    ];
  }

  // Validates the Pattern against the selected Pattern Type. Mirrors the micro-side check in
  // TpDataImportExcludedItemService.ValidatePatternFormat:
  //  - ThirdPartyId : any non-empty value.
  //  - FullEmail    : a valid email address.
  //  - EmailDomain  : a valid email domain.
  static validatePattern(value, editedItem) {
    const pattern = (value ?? '').toString().trim();
    if (pattern === '') return "This field can't be empty.";

    const domainRegex = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/i;
    const fullEmailRegex =
      /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/i;

    switch (editedItem.PatternType) {
      case 'FullEmail':
        if (!fullEmailRegex.test(pattern)) return "Enter a valid email address (e.g. 'name@example.com').";
        break;
      case 'EmailDomain':
        if (!domainRegex.test(pattern)) return "Enter a valid email domain (e.g. 'example.com').";
        break;
      default:
        // ThirdPartyId (or not yet chosen): any non-empty value is accepted.
        break;
    }
    return '';
  }

  static fieldsInEditPanel = [
    {
      fieldName: 'EntityType',
      label: 'Entity Type',
      className: 'col-6',
      type: InputTypesEnum.Select,
      options: TpDataImportExcludedItemPanelDescription.getEntityTypeOptions(),
      validityChecker: ValidityHelper.forRequiredTextField,
    },
    {
      fieldName: 'PatternType',
      label: 'Pattern Type',
      className: 'col-6',
      type: InputTypesEnum.Select,
      options: TpDataImportExcludedItemPanelDescription.getPatternTypeOptions(),
      validityChecker: ValidityHelper.forRequiredSelectField,
    },
    {
      fieldName: 'Pattern',
      label: 'Pattern',
      className: 'col-12',
      type: InputTypesEnum.Text,
      validityChecker: TpDataImportExcludedItemPanelDescription.validatePattern,
    },
    {
      // Bound to the third-party source NAME (what ThirdPartySourceSelect returns); the backend
      // resolves the name to the TpInstanceId when saving.
      fieldName: 'ThirdPartyInstanceName',
      label: 'Third-Party Instance',
      className: 'col-6',
      type: InputTypesEnum.ThirdPartySourceSelect,
      validityChecker: ValidityHelper.forRequiredTextField,
    },
    {
      fieldName: 'IsActive',
      label: 'Active',
      className: 'col-6',
      type: InputTypesEnum.Select,
      options: TpDataImportExcludedItemPanelDescription.getActiveOptions(),
      validityChecker: ValidityHelper.forRequiredBoolField,
    },
    {
      fieldName: 'Reason',
      label: 'Reason',
      className: 'col-12',
      type: InputTypesEnum.TextBox,
      numberOfRows: 3,
      validityChecker: ValidityHelper.forRequiredTextField,
    },
  ];
}
